const KEY_PREFIX = 'whatlanded:reviewer:';

type ReviewerRecord = {
  reviewer_id: string;
  finished: boolean;
  // High-water mark of client_seq for this reviewer, across ALL of their
  // sessions on this test. Persisted because reusing a reviewer_id without
  // continuing the sequence would restart at 1, collide with the previous
  // session's rows, and be silently discarded by the ON CONFLICT DO NOTHING that
  // makes retries idempotent. A dropped tap with no error is the worst outcome
  // available here.
  next_seq: number;
  // The regime the SERVER recorded for this reviewer at session start
  // (Cowork020 §2). Kept here so a returning reviewer resumes under the regime
  // they actually started in, even if the creator has since flipped the test —
  // which the removal of the mode lock now allows at any time.
  //
  // Null for records written before this existed, and for those the caller falls
  // back to the test's current mode. That is the same behaviour they had before,
  // so nothing regresses; only new sessions get the stronger guarantee.
  mode: 'blind' | 'live' | null;
};

// Remembers, per test, which reviewer this browser already is.
//
// Cowork016 §2. Every return visit used to create a fresh reviewer row, which
// inflated reviewer_count, flattened the retention curve with a viewer who
// watched nothing, and — worst — inflated `reach`. Reach exists precisely so a
// creator can tell "9 people reacted" from "1 person reacted 9 times"
// (Cowork004), so a duplicate row made it lie about the one thing it was added
// to be honest about.
//
// Keyed per test, so someone reviewing two different videos is two reviewers,
// which is correct.
//
// SAME-BROWSER ONLY, deliberately (Cowork016 §4). The alternative is putting a
// reviewer_id in a shareable URL, which turns it into a capability token: forward
// it once and someone who has not watched gets everyone's reactions. That is the
// contamination the whole gate exists to prevent.
//
// Every method swallows its own errors. Safari in private mode throws on
// localStorage writes, and losing a session to a storage exception would be far
// worse than losing the de-duplication.
export const reviewerSessionStorage = {
  read(testId: string): ReviewerRecord | null {
    try {
      const raw = window.localStorage.getItem(KEY_PREFIX + testId);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<ReviewerRecord>;
      if (typeof parsed?.reviewer_id !== 'string' || !parsed.reviewer_id) return null;
      return {
        reviewer_id: parsed.reviewer_id,
        finished: parsed.finished === true,
        next_seq: typeof parsed.next_seq === 'number' && parsed.next_seq > 0 ? parsed.next_seq : 1,
        mode: parsed.mode === 'blind' || parsed.mode === 'live' ? parsed.mode : null
      };
    } catch {
      return null;
    }
  },

  write(testId: string, record: ReviewerRecord): void {
    try {
      window.localStorage.setItem(KEY_PREFIX + testId, JSON.stringify(record));
    } catch {
      // Ignore: the session still works, it just will not be recognised later.
    }
  },

  clear(testId: string): void {
    try {
      window.localStorage.removeItem(KEY_PREFIX + testId);
    } catch {
      // Ignore.
    }
  }
};
